import { computed, onMounted, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { buildRequestErrorMessage } from './shared';

const normalizeStatus = (value) => (String(value || '').trim().toUpperCase() === 'TRUE' ? 'TRUE' : 'FALSE');

const normalizeItem = (item) => ({
  ucbox: String(item?.ucbox || '').trim(),
  status: normalizeStatus(item?.status),
  enabled: normalizeStatus(item?.status) === 'TRUE',
  redisKey: item?.redisKey || 'CrawlerBox',
});

const validateUcbox = (value) => {
  const ucbox = String(value || '').trim();
  if (ucbox.length !== 12) {
    throw new Error('测试接头长度必须为 12 位');
  }
  if (ucbox.includes(':')) {
    throw new Error('测试接头不能包含冒号');
  }
  return ucbox;
};

export function useMazdaCrawlerBox(mazdaPost, serverName) {
  const crawlerBoxItems = ref([]);
  const crawlerBoxTotal = ref(0);
  const crawlerBoxEnabledCount = ref(0);
  const crawlerBoxDisabledCount = ref(0);
  const crawlerBoxKeyword = ref('');
  const newCrawlerBox = ref('');
  const crawlerBoxLoading = ref(false);
  const crawlerBoxSaving = ref(false);

  const crawlerBoxFilteredItems = computed(() => {
    const keyword = String(crawlerBoxKeyword.value || '').trim().toLowerCase();
    if (!keyword) {
      return crawlerBoxItems.value;
    }
    return crawlerBoxItems.value.filter((item) => {
      return `${item.ucbox} ${item.status}`.toLowerCase().includes(keyword);
    });
  });

  const applyCrawlerBoxData = (data) => {
    crawlerBoxItems.value = Array.isArray(data.items) ? data.items.map(normalizeItem) : [];
    crawlerBoxTotal.value = Number(data.total ?? crawlerBoxItems.value.length);
    crawlerBoxEnabledCount.value = Number(data.enabledCount ?? crawlerBoxItems.value.filter((item) => item.status === 'TRUE').length);
    crawlerBoxDisabledCount.value = Number(data.disabledCount ?? Math.max(0, crawlerBoxTotal.value - crawlerBoxEnabledCount.value));
  };

  const loadCrawlerBoxes = async () => {
    crawlerBoxLoading.value = true;
    try {
      const response = await mazdaPost('crawlerBoxList');
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '测试接头读取失败');
        return;
      }
      applyCrawlerBoxData(data);
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('测试接头读取失败', error));
    } finally {
      crawlerBoxLoading.value = false;
    }
  };

  const submitAddCrawlerBox = async () => {
    let ucbox;
    try {
      ucbox = validateUcbox(newCrawlerBox.value);
    } catch (error) {
      ElMessage.error(error.message);
      return;
    }

    crawlerBoxSaving.value = true;
    try {
      const response = await mazdaPost('crawlerBoxAdd', { ucbox });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '测试接头添加失败');
        return;
      }
      applyCrawlerBoxData(data);
      newCrawlerBox.value = '';
      ElMessage.success(data.message || '测试接头已添加');
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('测试接头添加失败', error));
    } finally {
      crawlerBoxSaving.value = false;
    }
  };

  const updateCrawlerBoxStatus = async (row, status) => {
    if (!row?.ucbox) {
      return;
    }

    const nextStatus = normalizeStatus(status);
    crawlerBoxSaving.value = true;
    try {
      const response = await mazdaPost('crawlerBoxSetStatus', {
        ucbox: row.ucbox,
        status: nextStatus,
      });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '测试接头状态更新失败');
        await loadCrawlerBoxes();
        return;
      }
      applyCrawlerBoxData(data);
      ElMessage.success(data.message || '测试接头状态已更新');
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('测试接头状态更新失败', error));
      await loadCrawlerBoxes();
    } finally {
      crawlerBoxSaving.value = false;
    }
  };

  const deleteCrawlerBox = async (row) => {
    if (!row?.ucbox) {
      return;
    }

    try {
      await ElMessageBox.confirm(
        `确定删除测试接头吗？\n\n${row.ucbox}`,
        '确认删除',
        { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    crawlerBoxSaving.value = true;
    try {
      const response = await mazdaPost('crawlerBoxDelete', { ucbox: row.ucbox });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '测试接头删除失败');
        return;
      }
      applyCrawlerBoxData(data);
      ElMessage.success(data.message || '测试接头已删除');
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('测试接头删除失败', error));
    } finally {
      crawlerBoxSaving.value = false;
    }
  };

  watch(serverName, loadCrawlerBoxes);
  onMounted(loadCrawlerBoxes);

  return {
    crawlerBoxItems,
    crawlerBoxTotal,
    crawlerBoxEnabledCount,
    crawlerBoxDisabledCount,
    crawlerBoxKeyword,
    newCrawlerBox,
    crawlerBoxLoading,
    crawlerBoxSaving,
    crawlerBoxFilteredItems,
    loadCrawlerBoxes,
    submitAddCrawlerBox,
    updateCrawlerBoxStatus,
    deleteCrawlerBox,
  };
}
